import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/lib/api";
import { KPICard, Panel, PanelHeader, StatusDot } from "@/components/ui-bits";
import { INR, STAGE_META, STAGES, SPECIALIZATION_COLOR } from "@/lib/constants";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, RadialBarChart, RadialBar, BarChart, Bar } from "recharts";
import { TrendingUp, Users, Target, Activity, IndianRupee, Calendar } from "lucide-react";
import CoachDashboard from "@/pages/CoachDashboard";
import RevenueExpenseBreakdown from "@/components/RevenueExpenseBreakdown";
import PipelineProjectionChart from "@/components/PipelineProjectionChart";

const VOLT = "#CCFF00";

export default function DashboardPage() {
  const { user } = useAuth();
  // Coaches see a completely different, coach-specific dashboard
  if (user?.role === "COACH") return <CoachDashboard />;
  return <AdminDashboard />;
}

function AdminDashboard() {
  const { user, activeBranchId } = useAuth();
  const params = activeBranchId ? { branch_id: activeBranchId } : {};

  const overview = useQuery({
    queryKey: ["dash-overview", activeBranchId],
    queryFn: async () => (await apiClient.get("/dashboard/overview", { params })).data,
  });
  const funnel = useQuery({
    queryKey: ["dash-funnel", activeBranchId],
    queryFn: async () => (await apiClient.get("/dashboard/sales-funnel", { params })).data,
  });
  const trend = useQuery({
    queryKey: ["dash-trend", activeBranchId],
    queryFn: async () => (await apiClient.get("/dashboard/revenue-trend", { params })).data,
  });
  const coaches = useQuery({
    queryKey: ["dash-coaches", activeBranchId],
    queryFn: async () => (await apiClient.get("/dashboard/coach-performance", { params })).data,
  });

  const ov = overview.data || {};
  const capacity = ov.capacity || [];

  return (
    <div className="p-8 space-y-8 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Command Center · {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">
            Hello, {user?.full_name?.split(" ")[0]}.
          </h1>
          <div className="text-zinc-500 mt-1 text-sm">Operational pulse across NammaXFit · current-month KPIs.</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Today</div>
          <div className="font-display text-2xl font-bold text-white">
            {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPICard testId="kpi-active-members" label="Active Members" value={ov.active_members ?? "–"} sub={<><StatusDot /> <span className="ml-2">Currently enrolled · not expired</span></>} />
        <KPICard testId="kpi-new-members-mtd" label="New Members · MTD" value={ov.new_members_mtd ?? "–"} sub="Joined this month" />
        <KPICard testId="kpi-conversion" label="Lead Conversion · MTD" value={(ov.conversion_pct ?? 0) + "%"} sub={`${ov.won_leads || 0} won / ${ov.total_leads || 0} leads this month`} />
        <KPICard testId="kpi-new-revenue" label="New Member Revenue · MTD" value={INR(ov.revenue_new_members)} sub="First-time payments this month" />
        <KPICard testId="kpi-renewal-revenue" label="Renewal Revenue · MTD" value={INR(ov.revenue_renewals)} sub="Repeat payments this month" />
        <KPICard testId="kpi-at-risk" label="At Risk" value={ov.at_risk_members ?? "–"} accent="danger" sub="<60% attendance" />
      </div>

      {/* Trials row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard testId="kpi-trials-booked" label="Trials Booked · MTD" value={ov.trials_booked_mtd ?? "–"} sub="Includes downstream stages" />
        <KPICard testId="kpi-trials-completed" label="Trials Completed · MTD" value={ov.trials_completed_mtd ?? "–"} sub="Walked the floor this month" />
      </div>

      {/* Revenue + Expense Breakdown (Super Admin gets Total Revenue tile) */}
      <RevenueExpenseBreakdown />

      {/* Pipeline projection — next 12 months (renewals + new leads) */}
      <PipelineProjectionChart />

      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Sales funnel */}
        <Panel className="lg:col-span-7">
          <PanelHeader subtitle="Sales · This Month" title="Lead Pipeline" />
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={funnel.data || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#1f1f23" vertical={false} />
              <XAxis dataKey="stage" stroke="#52525B" fontSize={10}
                tickFormatter={(s) => STAGE_META[s]?.label?.split(" ")[0] || s} />
              <YAxis stroke="#52525B" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }}
                labelFormatter={(s) => STAGE_META[s]?.label || s} />
              <Bar dataKey="count" fill={VOLT} radius={[4, 4, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Capacity */}
        <Panel className="lg:col-span-5">
          <PanelHeader subtitle="Per Branch" title="Capacity Utilization" />
          <div className="space-y-4">
            {capacity.map((c) => (
              <div key={c.branch_id} data-testid={`capacity-${c.branch_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="font-semibold text-sm">{c.branch_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                      {c.active} / {c.capacity} active
                    </div>
                  </div>
                  <div className="font-display text-3xl font-black text-volt leading-none">
                    {c.utilization_pct}<span className="text-base text-zinc-500">%</span>
                  </div>
                </div>
                <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden">
                  <div className="h-full bg-volt transition-all duration-500"
                    style={{ width: `${Math.min(c.utilization_pct, 100)}%`, boxShadow: "0 0 12px rgba(204,255,0,0.5)" }} />
                </div>
              </div>
            ))}
            {capacity.length === 0 && <div className="text-zinc-600 text-sm">No branches yet.</div>}
          </div>
        </Panel>
      </div>

      {/* Row 2: revenue trend + coaches */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel className="lg:col-span-7">
          <PanelHeader subtitle="90-day" title="Membership Revenue Trend" />
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend.data || []}>
              <defs>
                <linearGradient id="volt-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={VOLT} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={VOLT} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1f1f23" vertical={false} />
              <XAxis dataKey="date" stroke="#52525B" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#52525B" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }}
                formatter={(v) => [INR(v), "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke={VOLT} strokeWidth={2} fill="url(#volt-grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel className="lg:col-span-5">
          <PanelHeader subtitle="Leaderboard" title="Coach Performance" />
          <div className="space-y-2 max-h-[300px] overflow-y-auto scrollbar-thin pr-1">
            {(coaches.data || []).slice(0, 7).map((c, i) => (
              <div key={c.coach_id} data-testid={`coach-row-${c.coach_id}`}
                className="flex items-center gap-3 p-2 bg-zinc-950 border border-zinc-900 rounded-sm hover:border-zinc-700 transition-colors">
                <div className="w-8 text-center font-display text-2xl font-black text-zinc-700">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-2 flex-wrap">
                    <span>{c.name}</span>
                    {(c.specializations || []).map((s) => (
                      <span key={s} className="text-[8px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm"
                        style={{ background: `${SPECIALIZATION_COLOR[s] || '#888'}22`, color: SPECIALIZATION_COLOR[s] || '#888' }}>
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    {c.members_assigned} members • {c.conversion_pct}% conv
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-bold text-volt leading-none">{c.performance_score}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Score</div>
                </div>
              </div>
            ))}
            {(coaches.data || []).length === 0 && <div className="text-zinc-600 text-sm">No coaches found.</div>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
